// どこから来た申込かの印（2026-11-05 代表了承の、検索広告とインバウンドの計測）。
// 広告や印つきのリンクから来たとき、URL の印（gclid・utm_source など）をこのブラウザに残し、資料請求・お問い合わせの
// 送信のときに受付（lambda/lead-api）へ渡す。サイトの中でページを移ると URL から印が消えるので、来たときに残しておく。
// 残すのは、最後に印つきで来たときの値だけで、90日たったら使わない（Google 広告の成果を数える期間の上限に合わせる）。
// 受付は gclid を使って、AI が「製品の相談」と分けた申込だけを、あとから Google 広告に成果として戻す
// （lambda/lead-api/scripts/export-ads-conversions.mjs）。氏名・メールなど個人を識別できる情報は、ここでは扱わない。
// 名前は受付の lambda/lead-api/src/validate.mjs の ATTRIBUTION_FIELDS と同じ（lambda/lead-api/test/contract.test.mjs で照合）。
// public/ の独立したページ（/trooper/ など。React の外）は、scripts/inject-analytics.js が同じ働きの小さなスクリプトを
// 入れる（保存の名前と形は同じ）。
export const ATTRIBUTION_STORAGE_KEY = 'salt2_attribution';
export const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

// URL の印の名前 → 受付に渡す名前
export const ATTRIBUTION_PARAMS = Object.freeze({
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  msclkid: 'msclkid',
  yclid: 'yclid',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
});

export const ATTRIBUTION_KEYS = Object.freeze([...Object.values(ATTRIBUTION_PARAMS), 'landingPage', 'capturedAt']);

const MAX_VALUE_LENGTH = 500;

// localStorage は、設定や閲覧のモードによっては使えない（例外になる）。使えなければ何も残さない
const defaultStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const isPrerender = () => typeof window !== 'undefined' && Boolean(window.__SALT2_PRERENDER__);

/** 今の URL に印があれば残す。残したものを返す（印が無ければ何もせず null）。 */
export function captureAttribution({
  location = typeof window !== 'undefined' ? window.location : null,
  storage = defaultStorage(),
  now = () => new Date(),
} = {}) {
  if (!location || isPrerender()) return null;
  const params = new URLSearchParams(location.search || '');
  const found = {};
  for (const [param, name] of Object.entries(ATTRIBUTION_PARAMS)) {
    const value = params.get(param);
    if (value) found[name] = value.slice(0, MAX_VALUE_LENGTH);
  }
  if (Object.keys(found).length === 0) return null;
  const value = { ...found, landingPage: `${location.origin}${location.pathname}`, capturedAt: now().toISOString() };
  try {
    storage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // 残せなくても、そのページで送る分は URL（page）に印が残る
  }
  return value;
}

/** 残してある印。無い・古い・壊れているときは {}。 */
export function getAttribution({ storage = defaultStorage(), now = () => new Date() } = {}) {
  let saved;
  try {
    saved = JSON.parse(storage?.getItem(ATTRIBUTION_STORAGE_KEY) || 'null');
  } catch {
    return {};
  }
  if (!saved || typeof saved !== 'object') return {};
  const capturedAt = Date.parse(saved.capturedAt);
  if (!Number.isFinite(capturedAt) || now().getTime() - capturedAt > ATTRIBUTION_MAX_AGE_MS) {
    try {
      storage?.removeItem(ATTRIBUTION_STORAGE_KEY);
    } catch {
      // 消せなくても、次に読むときにまた古いと分かる
    }
    return {};
  }
  const out = {};
  for (const key of ATTRIBUTION_KEYS) {
    if (typeof saved[key] === 'string' && saved[key]) out[key] = saved[key].slice(0, key === 'landingPage' ? 2000 : MAX_VALUE_LENGTH);
  }
  return out;
}
